import { echelleTemperatures, heureLocale } from '@/features/assets/components/courbeTemperature';

/**
 * Température mesurée et consigne d'une PAC sur 24 h, tirées de l'historique.
 *
 * DEUX SÉRIES, DONC UNE LÉGENDE : l'écart entre les deux courbes est ce qui se lit
 * (la pièce est-elle au-dessus de la consigne, donc la PAC à l'arrêt ?). La consigne est
 * en pointillé : c'est une valeur réglée, pas une mesure.
 *
 * Moins de deux points, aucune courbe : un tracé à point unique se lirait comme une
 * journée plate alors qu'on n'a qu'une mesure. Le texte le dit.
 */
const LARGEUR = 320;
const HAUTEUR = 90;
const MARGE_BAS = 14;
const MARGE_HAUT = 8;

export default function CourbeTemperaturePac({ points = [] }) {
  const mesures = (points || []).filter(p => p.temperature_mesuree != null);
  if (mesures.length < 2) {
    return (
      <p className="text-[11px] text-neutral-400" data-testid="pac-courbe-vide">
        Pas encore assez de relevés sur 24 h pour tracer une courbe.
      </p>
    );
  }
  const { min, max } = echelleTemperatures(mesures);
  const utile = HAUTEUR - MARGE_BAS - MARGE_HAUT;
  const x = i => (i / (mesures.length - 1)) * LARGEUR;
  const y = v => MARGE_HAUT + utile - ((v - min) / (max - min)) * utile;
  const trace = cle => mesures
    .map((p, i) => (p[cle] == null ? null : `${x(i).toFixed(1)},${y(p[cle]).toFixed(1)}`))
    .filter(Boolean)
    .map((xy, i) => `${i ? 'L' : 'M'}${xy}`)
    .join(' ');
  const dernier = mesures.length - 1;

  return (
    <div data-testid="pac-courbe">
      <svg viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`} className="w-full h-auto" role="img"
           aria-label={`Température mesurée et consigne, de ${heureLocale(mesures[0].at)} à ${heureLocale(mesures[dernier].at)}`}>
        <path d={trace('temperature_consigne')} fill="none" stroke="#a3a3a3" strokeWidth="1.2" strokeDasharray="3 3" />
        <path d={trace('temperature_mesuree')} fill="none" stroke="#ea580c" strokeWidth="1.6" />
        {[0, Math.floor(dernier / 2), dernier].map(i => (
          <text key={i} x={x(i)} y={HAUTEUR - 2} fontSize="8" fill="#a3a3a3"
                textAnchor={i === 0 ? 'start' : i === dernier ? 'end' : 'middle'}>
            {heureLocale(mesures[i].at)}
          </text>
        ))}
        <text x={2} y={MARGE_HAUT + 2} fontSize="8" fill="#a3a3a3">{max.toFixed(0)} °C</text>
        <text x={2} y={HAUTEUR - MARGE_BAS} fontSize="8" fill="#a3a3a3">{min.toFixed(0)} °C</text>
      </svg>
      <div className="flex gap-4 text-[10px] text-neutral-500 mt-1">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-orange-600 inline-block" /> mesurée</span>
        <span className="flex items-center gap-1"><span className="w-3 border-t border-dashed border-neutral-400 inline-block" /> consigne</span>
        <span className="ml-auto">{mesures.length} relevés</span>
      </div>
    </div>
  );
}
